import { inject } from "@angular/core";
import { Router } from "@angular/router";

import { firstValueFrom } from "rxjs";

import { AuthService } from "@core/services/auth.service";
import { CookieService } from "@core/services/cookie.service";
import { LoggerService } from "@core/services/logger.service";
import { DocumentationRoutesConstants } from "@documentation/routes/documentation.routes.constants";
import { ModalService } from "@shared/components/modal/modal.service";
import { User } from "@shared/models/user.model";

export const publicGuard = async (): Promise<boolean> => {
  const authService = inject(AuthService);
  const cookieService = inject(CookieService);
  const modalService = inject(ModalService);
  const logger = inject(LoggerService);
  const router = inject(Router);

  // If app already knows user is logged in, redirect without calling API
  const cachedUser = authService.currentUser();
  if (cachedUser) {
    router.navigate([DocumentationRoutesConstants.main()]).then();
    return false;
  }

  // If no token, check if we should create a temp user
  if (!authService.accessToken) {
    // Check if this is an invite flow (has organizationId and email parameters)
    const urlParams = new URLSearchParams(window.location.search);
    const isInviteFlow = urlParams.has("organizationId") && urlParams.has("email");

    if (!isInviteFlow) {
      // Check if user has already had a temp account
      const hadTempUser = cookieService.getCookie("had_temp_user");

      if (hadTempUser !== "true") {
        // First-time visitor, create temp user and redirect to main app
        logger.debug("First-time visitor on public page, creating guest user");
        const guestCreated = await authService.ensureGuest();

        if (guestCreated) {
          // Show welcome modal and redirect to main app
          logger.debug("Guest user created, showing welcome modal and redirecting to main app");
          setTimeout(() => modalService.openWelcomeModal(false), 300); // Delay to ensure routing completes
          await router.navigate([DocumentationRoutesConstants.main()]);
          return false;
        }

        // If guest creation failed, allow access to login page
        logger.debug("Guest creation failed, showing login page");
      }
    } else {
      // This is an invite flow, don't create a guest user
      logger.debug("Invite flow detected, skipping guest user creation");
    }

    // User already had temp account, guest creation failed, or invite flow - show login page
    return true;
  }

  try {
    // Token exists; verify it once via API
    const user: User | null = await firstValueFrom(authService.getMe());
    if (user) {
      router.navigate([DocumentationRoutesConstants.main()]).then();
      return false;
    }
    return true;
  } catch {
    return true; // Allow access if auth check fails
  }
};
